import React, { useCallback, useRef, useState } from 'react';
import { LuFileUp, LuGlobe, LuText } from 'react-icons/lu';
import { apiErrorMessage } from '../../utils/multipartUpload';
import LearnPdfImportPanel from './LearnPdfImportPanel';

const MODES = [
  { id: 'pdf', label: 'PDF', icon: LuFileUp },
  { id: 'text', label: 'Text', icon: LuText },
  { id: 'url', label: 'URL', icon: LuGlobe },
];

const MIN_TEXT = 80;

export default function LearnNotebookImportSection({
  notebookId,
  apiClient,
  onJobStarted,
  onError,
  compact = false,
  defaultMode = 'pdf',
}) {
  const [mode, setMode] = useState(defaultMode);
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const pdfPanelRef = useRef(null);

  const importText = async () => {
    if (text.trim().length < MIN_TEXT) {
      onError?.(`Paste at least ${MIN_TEXT} characters.`);
      return;
    }
    setBusy(true);
    onError?.('');
    try {
      const res = await apiClient.post('/api/v1/imports/text', {
        notebookId: Number(notebookId),
        text: text.trim(),
        questionCount: 'AUTO',
      });
      onJobStarted?.(res.data);
      setText('');
    } catch (e) {
      onError?.(apiErrorMessage(e, 'Text import failed'));
    } finally {
      setBusy(false);
    }
  };

  const importUrl = async () => {
    if (!url.trim()) {
      onError?.('Enter a public article URL.');
      return;
    }
    setBusy(true);
    onError?.('');
    try {
      const res = await apiClient.post('/api/v1/imports/url', {
        notebookId: Number(notebookId),
        url: url.trim(),
        questionCount: 'AUTO',
      });
      onJobStarted?.(res.data);
      setUrl('');
    } catch (e) {
      onError?.(apiErrorMessage(e, 'URL import failed'));
    } finally {
      setBusy(false);
    }
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (mode !== 'pdf') setMode('pdf');
    const file = e.dataTransfer?.files?.[0];
    if (file && pdfPanelRef.current?.pickFile) {
      pdfPanelRef.current.pickFile(file);
    }
  }, [mode]);

  const textLen = text.trim().length;
  const textOk = textLen >= MIN_TEXT;

  return (
    <section
      className={`learn-import-section${compact ? ' learn-import-section--compact' : ''}${dragOver ? ' learn-import-section--drag' : ''}`}
      aria-labelledby="learn-import-heading"
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
    >
      <div className="learn-import-header">
        <div className="learn-import-header-copy">
          <h2 id="learn-import-heading" className="learn-import-heading">
            Add content to this notebook
          </h2>
          <p className="learn-muted learn-import-lead">
            PDFs are analyzed first (pages, extractable text), then you confirm options. Text and URL imports run in the background.
          </p>
        </div>
        <div className="learn-import-tabs" role="tablist" aria-label="Import type">
          {MODES.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={mode === id}
              className={`learn-import-tab${mode === id ? ' learn-import-tab--active' : ''}`}
              onClick={() => { setMode(id); onError?.(''); }}
              disabled={busy}
            >
              <Icon aria-hidden />
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="learn-import-panel" role="tabpanel">
        {mode === 'pdf' && (
          <LearnPdfImportPanel
            ref={pdfPanelRef}
            notebookId={notebookId}
            apiClient={apiClient}
            onJobStarted={onJobStarted}
            onError={onError}
            busy={busy}
            setBusy={setBusy}
            dragOver={dragOver}
          />
        )}

        {mode === 'text' && (
          <>
            <textarea
              className="learn-input learn-import-textarea"
              rows={compact ? 5 : 8}
              placeholder="Paste chapter notes, meeting notes, or study material…"
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={busy}
              aria-describedby="learn-text-count"
            />
            <div className="learn-import-text-footer">
              <p id="learn-text-count" className={`learn-import-char-count${textOk ? ' learn-import-char-count--ok' : ''}`}>
                {textLen} / {MIN_TEXT}+ characters
                {!textOk && textLen > 0 ? (
                  <span className="learn-import-char-hint">
                    {' '}
                    · add {MIN_TEXT - textLen} more to enable
                  </span>
                ) : null}
              </p>
              <button
                type="button"
                className="home-btn home-btn-primary learn-btn--compact"
                disabled={busy || !textOk}
                onClick={importText}
                aria-describedby="learn-text-count"
              >
                {busy ? 'Importing…' : 'Generate from text'}
              </button>
            </div>
          </>
        )}

        {mode === 'url' && (
          <>
            <input
              type="url"
              className="learn-input"
              placeholder="https://example.com/article"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={busy}
              onKeyDown={(e) => e.key === 'Enter' && importUrl()}
            />
            <p className="learn-muted learn-import-hint">Public HTTP(S) only. Private networks are blocked.</p>
            <button
              type="button"
              className="home-btn home-btn-primary learn-btn--compact"
              disabled={busy || !url.trim()}
              onClick={importUrl}
            >
              {busy ? 'Fetching…' : 'Import from URL'}
            </button>
          </>
        )}
      </div>
    </section>
  );
}
